import Phaser from "phaser";
import { MAP_TEXTURE } from "../config/rendering";
import { MAPS } from "../config/map";

function seeded(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value * 1_664_525 + 1_013_904_223) >>> 0;
    return value / 4_294_967_296;
  };
}

export function createCastleApproachTextures(scene: Phaser.Scene): void {
  if (scene.textures.exists(MAP_TEXTURE.key)) return;
  const random = seeded(15_001),
    graphics = scene.add.graphics(),
    width = MAP_TEXTURE.width,
    height = MAP_TEXTURE.height,
    tile = MAP_TEXTURE.logicalTileSize;
  graphics.fillStyle(0x596f3f).fillRect(0, 0, width, height);
  // Fine terrain grain and faint tile seams keep the battlefield legible without looking flat.
  graphics.lineStyle(1, 0x253320, 0.09);
  for (let x = 0; x <= width; x += tile) graphics.lineBetween(x, 0, x, height);
  for (let y = 0; y <= height; y += tile) graphics.lineBetween(0, y, width, y);
  for (let index = 0; index < 520; index += 1) {
    const x = random() * width,
      y = random() * height,
      length = 2 + random() * 7;
    graphics
      .lineStyle(1, random() > 0.5 ? 0xa8b978 : 0x263c2a, 0.12 + random() * 0.16)
      .lineBetween(x, y, x + random() * 3 - 1.5, y - length);
  }
  graphics
    .fillStyle(0x718451, 0.18)
    .fillEllipse(width * 0.3, height * 0.34, width * 0.38, height * 0.3)
    .fillEllipse(width * 0.62, height * 0.58, width * 0.45, height * 0.34)
    .fillEllipse(width * 0.48, height * 0.78, width * 0.28, height * 0.16);
  graphics
    .fillStyle(0x435a36, 0.55)
    .fillRect(0, 0, width, tile * 0.85)
    .fillRect(0, height - tile * 0.8, width, tile * 0.8);
  for (let index = 0; index < 250; index += 1) {
    const x = random() * width,
      y = random() * height,
      radius = 2 + random() * 11;
    graphics
      .fillStyle(random() > 0.52 ? 0x8da05d : 0x344c31, 0.08 + random() * 0.14)
      .fillEllipse(x, y, radius * 2.4, radius);
  }
  for (let index = 0; index < 34; index += 1) {
    const x = random() * width,
      y = random() * height;
    graphics
      .lineStyle(1 + random() * 2, 0xb1bd76, 0.25)
      .lineBetween(x, y, x + random() * 8 - 4, y - 4 - random() * 7);
  }
  graphics
    .fillStyle(0x6f5738, 0.26)
    .fillEllipse(tile * 5.8, tile * 11.8, tile * 5.4, tile * 2.3)
    .fillEllipse(tile * 15.6, tile * 4.2, tile * 6.2, tile * 2.6);
  graphics
    .fillStyle(0x3b4f35, 0.45)
    .fillEllipse(tile * 9, tile * 2, tile * 3.3, tile * 1.2)
    .fillEllipse(tile * 14, tile * 13.8, tile * 3.8, tile * 1.15);

  // Natural stone boundary and breached western palisade.
  graphics
    .fillStyle(0x3a2d21)
    .fillRect(0, 0, 13, height)
    .fillRect(width - 18, 0, 18, height);
  for (let y = 0; y < 16; y += 1) {
    if (y !== 8) {
      graphics
        .fillStyle(y % 2 ? 0x796b58 : 0x897a63)
        .fillRoundedRect(width - 23, y * tile + 2, 25, tile - 4, 4);
      graphics
        .lineStyle(2, 0x302820, 0.55)
        .strokeRoundedRect(width - 23, y * tile + 2, 25, tile - 4, 4);
    }
  }
  graphics
    .fillStyle(0x493423)
    .fillRect(0, tile * 6.5, 15, tile * 1.2)
    .fillRect(0, tile * 9.3, 15, tile * 1.2);
  graphics.fillStyle(0x201a15).fillRoundedRect(0, tile * 7.2, tile * 0.55, tile * 2.6, 10);

  // Lion Gate and its apron are aligned to exit (23, 8).
  graphics
    .fillStyle(0x655b4d)
    .fillRoundedRect(width - tile * 0.9, tile * 6.75, tile * 0.9, tile * 3.5, 8);
  graphics
    .fillStyle(0x171613)
    .fillRoundedRect(width - tile * 0.72, tile * 7.35, tile * 0.72, tile * 2.3, 18);
  graphics
    .lineStyle(4, 0xc6a95a)
    .strokeRoundedRect(width - tile * 0.72, tile * 7.35, tile * 0.72, tile * 2.3, 18);
  graphics
    .fillStyle(0x8c744c, 0.38)
    .fillTriangle(width - tile * 2.8, tile * 7.7, width, tile * 7.3, width, tile * 9.7);

  // Exact logical obstacles: northern ruin, old well, southern oak cluster.
  graphics
    .fillStyle(0x746d5d)
    .fillRect(tile * 5.05, tile * 3.05, tile * 1.8, tile * 0.7)
    .fillRect(tile * 5.05, tile * 3.1, tile * 0.62, tile * 1.72);
  graphics.lineStyle(3, 0x39362f).strokeRect(tile * 5.05, tile * 3.05, tile * 1.8, tile * 0.7);
  graphics
    .fillStyle(0x423a2e)
    .fillCircle(tile * 11.5, tile * 7.5, tile * 0.34)
    .lineStyle(6, 0x90826a)
    .strokeCircle(tile * 11.5, tile * 7.5, tile * 0.31);
  for (const [x, y, size] of [
    [17.45, 11.45, 0.55],
    [18.45, 11.45, 0.62],
    [18.45, 12.45, 0.5],
  ] as const) {
    graphics.fillStyle(0x3a2c1f).fillRect(tile * x - 5, tile * y, 10, tile * 0.45);
    graphics
      .fillStyle(0x263e28)
      .fillCircle(tile * x, tile * y, tile * size)
      .fillStyle(0x4f693b, 0.8)
      .fillCircle(tile * (x - 0.14), tile * (y - 0.12), tile * size * 0.72);
  }
  for (let index = 0; index < 18; index += 1) {
    const x = tile * (1.5 + random() * 20.5),
      y = tile * (0.8 + random() * 14.3);
    graphics
      .fillStyle(index % 3 === 0 ? 0xd7bd56 : 0xd8d2b1, 0.65)
      .fillCircle(x, y, 1.5 + random() * 1.6);
  }
  // Castle Approach production pass: stone apron, worn verge, and richer foreground detail.
  graphics.fillStyle(0x9a8d72, 0.32);
  graphics.fillRoundedRect(width - tile * 3.3, tile * 7.05, tile * 2.35, tile * 2.9, 12);
  graphics.lineStyle(2, 0x5a5145, 0.32);
  for (let row = 0; row < 5; row += 1) {
    const y = tile * 7.25 + row * tile * 0.52;
    for (let column = 0; column < 4; column += 1) {
      const x = width - tile * 3.1 + column * tile * 0.52 + (row % 2) * tile * 0.24;
      graphics.strokeRoundedRect(x, y, tile * 0.45, tile * 0.34, 5);
    }
  }
  graphics.fillStyle(0x2f432d, 0.28);
  graphics.fillEllipse(tile * 3.2, tile * 13.4, tile * 4.8, tile * 1.2);
  graphics.fillEllipse(tile * 20.2, tile * 2.2, tile * 4.2, tile * 1.15);
  graphics.fillStyle(0xd8c46f, 0.5);
  for (let index = 0; index < 26; index += 1) {
    const x = tile * (1 + random() * 21.5);
    const y = tile * (0.8 + random() * 14.2);
    graphics.fillCircle(x, y, 1 + random() * 1.4);
  }
  graphics.generateTexture(MAP_TEXTURE.key, width, height);
  graphics.destroy();

  const gate = scene.add.graphics();
  gate
    .fillStyle(0x15120f, 0.6)
    .fillEllipse(48, 54, 76, 24)
    .fillStyle(0x513722)
    .fillRect(8, 13, 12, 43)
    .fillRect(76, 13, 12, 43)
    .fillStyle(0x7b5832)
    .fillTriangle(4, 18, 48, 0, 92, 18)
    .lineStyle(4, 0x2c2118)
    .strokeTriangle(4, 18, 48, 0, 92, 18)
    .fillStyle(0x171411)
    .fillRoundedRect(20, 16, 56, 43, 18)
    .lineStyle(3, 0xd0ad52)
    .strokeRoundedRect(20, 16, 56, 43, 18);
  gate.generateTexture("landmark-entry", 96, 64);
  gate.destroy();
  const banner = scene.add.graphics();
  banner
    .fillStyle(0x36281d)
    .fillRect(4, 0, 5, 58)
    .fillStyle(0xb22f36)
    .fillTriangle(9, 7, 44, 14, 9, 31)
    .fillStyle(0xe7c55d)
    .fillCircle(19, 17, 4);
  banner.generateTexture("map-banner", 48, 64);
  banner.destroy();
  const torch = scene.add.graphics();
  torch
    .fillStyle(0x4c3321)
    .fillRect(12, 22, 5, 34)
    .fillStyle(0xf3bd3e, 0.55)
    .fillCircle(14, 16, 13)
    .fillStyle(0xffe080)
    .fillTriangle(8, 19, 15, 0, 21, 20);
  torch.generateTexture("map-torch", 30, 60);
  torch.destroy();

  const grass = scene.add.graphics();
  grass.lineStyle(2, 0x355a35, 0.95);
  grass.lineBetween(12, 28, 9, 10);
  grass.lineBetween(12, 28, 15, 6);
  grass.lineBetween(12, 28, 21, 12);
  grass.lineStyle(1, 0x7fa05b, 0.7);
  grass.lineBetween(12, 28, 5, 15);
  grass.generateTexture("environment-grass-tuft", 28, 32);
  grass.destroy();

  const flower = scene.add.graphics();
  flower.fillStyle(0x3d6d39, 1);
  flower.fillRect(8, 10, 2, 12);
  flower.fillStyle(0xf0d778, 1);
  flower.fillCircle(9, 8, 3);
  flower.fillStyle(0xe9efe2, 0.9);
  flower.fillCircle(6, 8, 2);
  flower.fillCircle(12, 8, 2);
  flower.fillCircle(9, 5, 2);
  flower.fillCircle(9, 11, 2);
  flower.generateTexture("environment-flower", 18, 24);
  flower.destroy();

  const butterfly = scene.add.graphics();
  butterfly.fillStyle(0xf0c75a, 0.95);
  butterfly.fillTriangle(8, 8, 1, 2, 2, 12);
  butterfly.fillTriangle(10, 8, 17, 2, 16, 12);
  butterfly.fillStyle(0x513b28, 1);
  butterfly.fillRect(8, 5, 2, 8);
  butterfly.generateTexture("environment-butterfly", 18, 14);
  butterfly.destroy();

  const firefly = scene.add.graphics();
  firefly.fillStyle(0xe9f7a8, 0.22);
  firefly.fillCircle(8, 8, 8);
  firefly.fillStyle(0xf4ff9b, 0.95);
  firefly.fillCircle(8, 8, 2);
  firefly.generateTexture("environment-firefly", 16, 16);
  firefly.destroy();

  const palettes: Record<string, [number, number, number]> = {
    "royal-courtyard": [0x77776d, 0x949584, 0x5c5d56],
    "siege-grounds": [0x66543c, 0x806b4e, 0x40362c],
    "enchanted-forest": [0x365a3c, 0x527a51, 0x253d31],
    "frozen-keep": [0x9db6c3, 0xc7d7dc, 0x596d78],
    "coastal-citadel": [0x6c826f, 0x899b7f, 0x3e5968],
    "underground-stronghold": [0x4d4a48, 0x625d59, 0x2f2c31],
    "volcanic-fortress": [0x3b3330, 0x5a4540, 0x7b2f21],
  };
  for (const map of Object.values(MAPS)) {
    if (map.id === "castle-approach" || scene.textures.exists(map.backgroundAsset)) continue;
    const [base, light, dark] = palettes[map.id] ?? [0x596f3f, 0x718451, 0x344c31];
    const g = scene.add.graphics();
    const w = map.width * MAP_TEXTURE.logicalTileSize;
    const h = map.height * MAP_TEXTURE.logicalTileSize;

    let mapSeed = 0;

    for (const character of map.id) {
      mapSeed += character.charCodeAt(0);
    }

    const rnd = seeded(mapSeed);
    g.fillStyle(base).fillRect(0, 0, w, h);
    for (let i = 0; i < 180; i++) {
      const x = rnd() * w,
        y = rnd() * h,
        r = 3 + rnd() * 18;
      g.fillStyle(rnd() > 0.5 ? light : dark, 0.08 + rnd() * 0.16).fillEllipse(x, y, r * 2.4, r);
    }
    g.fillStyle(dark, 0.55)
      .fillRect(0, 0, 14, h)
      .fillRect(w - 18, 0, 18, h);
    for (const obstacle of map.obstacles)
      for (const cell of obstacle.cells)
        g.fillStyle(dark, 0.72).fillRoundedRect(cell.x * 48 + 5, cell.y * 48 + 5, 38, 38, 8);
    for (const entrance of map.entrances)
      g.fillStyle(0x3b281d, 0.8).fillRoundedRect(
        entrance.cell.x * 48,
        entrance.cell.y * 48 + 5,
        48,
        38,
        10,
      );
    for (const exit of map.exits)
      g.fillStyle(0xd2b34f, 0.75).fillRoundedRect(
        exit.cell.x * 48 + 3,
        exit.cell.y * 48 + 3,
        42,
        42,
        7,
      );

    g.lineStyle(1, dark, 0.16);
    for (let x = 0; x <= w; x += 48) g.lineBetween(x, 0, x, h);
    for (let y = 0; y <= h; y += 48) g.lineBetween(0, y, w, y);
    for (let i = 0; i < 380; i += 1) {
      const x = rnd() * w,
        y = rnd() * h,
        size = 1 + rnd() * 4;
      g.fillStyle(rnd() > 0.55 ? light : dark, 0.15 + rnd() * 0.22).fillEllipse(
        x,
        y,
        size * 2.2,
        size,
      );
    }
    // Map-specific landmark language.
    if (map.id === "frozen-keep") {
      for (let i = 0; i < 22; i += 1)
        g.lineStyle(2, 0xe9f5fa, 0.3).lineBetween(rnd() * w, rnd() * h, rnd() * w, rnd() * h);
    } else if (map.id === "volcanic-fortress") {
      for (let i = 0; i < 16; i += 1) {
        const x = rnd() * w,
          y = rnd() * h;
        g.fillStyle(0xe65b2d, 0.3)
          .fillCircle(x, y, 4 + rnd() * 10)
          .fillStyle(0xffc15e, 0.25)
          .fillCircle(x, y, 2 + rnd() * 5);
      }
    } else if (map.id === "coastal-citadel") {
      g.fillStyle(0x335f78, 0.38).fillRect(0, 0, w, 30);
      for (let i = 0; i < 18; i += 1)
        g.lineStyle(2, 0xbadbe6, 0.24).lineBetween(
          rnd() * w,
          5 + rnd() * 22,
          rnd() * w,
          5 + rnd() * 22,
        );
    } else if (map.id === "enchanted-forest") {
      for (let i = 0; i < 24; i += 1)
        g.fillStyle(0xa8e89b, 0.18).fillCircle(rnd() * w, rnd() * h, 2 + rnd() * 6);
    }
    g.generateTexture(map.backgroundAsset, w, h);
    g.destroy();
  }
}
